import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from '../components/Footer';
import { BBB_PROFILE_URL } from '../components/BBBSeal';

interface FAQItem {
  question: string;
  answer: string;
  answerContent?: React.ReactNode;
  slug?: string;
}

interface FAQCategory {
  id: string;
  title: string;
  questions: FAQItem[];
}

const FAQPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>('pricing');
  const [selectedQuestion, setSelectedQuestion] = useState<{ question: string; answer: string; answerContent?: React.ReactNode; scrollId?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories: FAQCategory[] = useMemo(() => [
    {
      id: 'pricing',
      title: 'Pricing',
      questions: [
        {
          question: 'What is it per month/do you pay a monthly fee? What is your medical alert pricing?',
          answer: 'See our pricing page or call us at 1-877-579-9862.'
        },
        {
          question: 'What is the clear advantage to paying today?',
          answer: 'At Alert1, we always offer competitive pricing and great promotions. Paying today will ensure you don\'t miss out on any of our great deals!'
        },
        {
          question: 'What is the clear advantage to paying annually?',
          answer: 'Paying annually means you are covered for an entire year and don\'t have to worry about monthly payments. Plus, you get our best deals and promotions!'
        },
        {
          question: 'Is this covered by insurance? Does Medicare or Medicaid pay for this?',
          answer: 'We encourage our members to contact their health insurance provider to see if their plan will issue a reimbursement for a medical alert system. Alert1 accepts payment from credit cards, debit cards, and checks through automatic withdrawal. You can find more information on our medicare page.'
        },
        {
          question: 'Do you offer any deals for spouses or partners?',
          answer: 'Yes we offer deals for spouses or partners. Call to learn more at 1-877-579-9862.'
        },
        {
          question: 'Do you offer any deals for an additional user?',
          answer: 'Yes! You get a discounted price on a second unit or button to cover another person in your home.'
        },
        {
          question: 'Do you have to buy the equipment?',
          answer: 'No, we do not charge for equipment.'
        },
        {
          question: 'What payment discounts do you offer?',
          answer: 'We offer an AAA, AARP discount, veteran, and a refer-a-friend discount. You can get a free month of service for every friend you refer. Learn more on our refer a friend page or call 1-877-579-9862.',
          answerContent: <>We offer an AAA, AARP discount, veteran, and a refer-a-friend discount. You can get a free month of service for every friend you refer. Learn more on our <Link to="/refer-friend" style={{ color: '#0066cc', textDecoration: 'underline' }}>refer a friend page</Link> or call 1-877-579-9862.</>
        },
        {
          question: 'Do you charge me if I press my help button?',
          answer: 'No, we do not charge for button presses or for sending help.'
        },
        {
          question: 'Will my rates go up if I push my help button more?',
          answer: 'We offer unlimited usage with all our medical alert service plans.'
        },
        {
          question: 'Are there any other hidden fees?',
          answer: 'Alert1 has no hidden fees for our medical alert service. Some products may have a one time activation fee depending on our current promotion.'
        },
        {
          question: 'Which credit cards do you accept?',
          answer: 'We accept all major credit cards, including Visa, Mastercard, Discover, and American Express. We can also debit your checking account if you prefer—please call us to set this up, as our website does not allow this payment method.'
        },
        {
          question: 'Can I have my checking account set up to pay my bill?',
          answer: 'Yes, you can. Please call us to set this up, as our website does not allow this payment method.'
        },
        {
          question: 'Do you offer refunds?',
          answer: 'Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.'
        },
        {
          question: 'Do you have a trial period?',
          answer: 'Alert1 allows any user to cancel service at any time. However, no refunds are offered unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.'
        },
        {
          question: 'Will I receive a reminder that my annual renewal date was coming up?',
          answer: 'Yes. We send a courtesy billing reminder via email thirty days before your anniversary date.'
        },
        {
          question: 'What address do I mail my payment to?',
          answer: 'Mail to: 1000 Commerce Park Drive, Suite 300 Williamsport, Pennsylvania 17701. You can also pay online. Please call 1-877-579-9862 to update your billing information.'
        }
      ]
    },
    {
      id: 'contract',
      title: 'Contract Questions',
      questions: [
        {
          question: 'How long is a typical medical monitoring system agreement?',
          answer: 'The typical industry agreement is three years. We offer plans from one month to three years.'
        },
        {
          question: 'Do you have a contract?',
          answer: 'With Alert1, there is never a long term contract required. You can choose the payment plan that best fits your needs.'
        },
        {
          question: 'Does the medical monitoring system come with a guarantee/warranty?',
          answer: 'We offer Alert1 Warranty Advantage Plans should anything happen to your system. We will replace your On-The-Go button or In-Home unit free of charge if your system malfunctions.'
        },
        {
          question: 'How do I send the equipment back?',
          answer: 'To cancel your service and return the equipment, please call us at 1-800-693-5433 and select option 2.',
          slug: 'equipment-back'
        }
      ]
    },
    {
      id: 'product',
      title: 'Product Questions',
      questions: [
        {
          question: 'What system would fit me best?',
          answer: 'That depends on your lifestyle and level of comfort. Learn more on our product overview page.',
          answerContent: <>That depends on your lifestyle and level of comfort. Learn more on our <Link to="/medical-alerts" style={{ color: '#0066cc', textDecoration: 'underline' }}>product overview page</Link>.</>
        },
        {
          question: 'Are your medical alerts compatible with pacemakers?',
          answer: 'Yes, all Alert1 medical alerts are compatible with pacemakers.'
        },
        {
          question: 'Does the button have a microphone in it?',
          answer: 'All of our on-the-go alerts, including LifeBeacon and others, have microphones in the help button.'
        },
        {
          question: 'How loud is the medical alert system?',
          answer: 'Loud enough for you to hear it from far away. View our product pages for the specific decibel levels of each of our medical alerts.'
        },
        {
          question: 'What is fall detection?',
          answer: 'The fall detection pendant can automatically call for help once a fall is sensed. Fall detection does not detect 100% of falls—we recommend always pressing the help button in an emergency.'
        },
        {
          question: 'Are the sensors in the fall pendant too sensitive?',
          answer: 'The fall detection is calibrated with five advanced gyroscopes that detect velocity and direction. If your device seems to be too sensitive, call us and we can help calibrate it. Fall detection pendants should be worn at heart level and outside of clothing.'
        },
        {
          question: 'Do wristbands come with the system? How do they work with fall detection?',
          answer: 'Yes! You can get a free wristband with our Home Fall Detection Medical Alert. The wristband is a great alternative to the pendant. It is small and comfortable - perfect for wearing while sleeping to increase comfort and reduce the risk of false alarms during the night.'
        },
        {
          question: 'Is it ugly or bulky? Can they wear it on their ___?',
          answer: 'You can wear your alert bracelet or safety pendant as a necklace, wristband, or belt clip.'
        },
        {
          question: 'How much do your medical alerts weigh?',
          answer: 'It depends on the system, but most of our medical alert pendants weigh only a couple of ounces or less. View our products to learn more.'
        },
        {
          question: 'How large is the help pendant? How big is it? What are the dimensions?',
          answer: 'All our medical alert systems are small, convenient and wearable. Our home system pendants are about one inch long by a half inch wide. To learn more, visit our product pages.'
        },
        {
          question: 'Do you sell medical I.D. bracelets?',
          answer: 'Yes. You can buy a medical I.D. bracelet on our accessory page.'
        },
        {
          question: 'Can I get a fall detection bracelet or a mobile bracelet?',
          answer: 'Our fall detection pendants—uniquely in the industry—can be used either as a pendant or a wristband.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Questions',
      questions: [
        {
          question: 'How long does the battery last?',
          answer: 'This varies widely by product and by use-case. Fall detection dramatically shortens battery life for any type of pendant, for example. However, batteries for traditional medical alert pendants (not on-the-go and not fall detection enabled) typically last up to 3 years, although we recommend testing at least once a week. A traditional alert with fall detection has a pendant battery life of closer to one year, although this can vary widely depending on activity levels, and we recommend testing the pendant every other day. Mobile medical alerts have a battery life of roughly five days under typical use patterns, but we recommend charging each night. The device will audibly warn the user when the battery is low. In fall detection scenarios, recharging each night is imperative, as battery life shortens to roughly 24 hours.'
        },
        {
          question: 'How do I know if my medical alert system is working?',
          answer: 'You can test your system anytime by pressing the button. Tell the operator you are testing. We recommend testing weekly.'
        },
        {
          question: 'What if my power goes out?',
          answer: 'Our in-home systems have backup batteries that last 24-48 hours during power outages.'
        },
        {
          question: 'Why are the lights flashing on my device?',
          answer: 'Flashing lights indicate different statuses. Please refer to your user manual or call us at 1-877-579-9862 for assistance.'
        }
      ]
    },
    {
      id: 'where-it-works',
      title: 'Where It Works/Coverage Questions',
      questions: [
        {
          question: 'Where does my medical alert work?',
          answer: 'Our in-home systems work throughout your home and up to 1000 feet from the base unit. Our mobile systems work nationwide on AT&T or T-Mobile networks.'
        },
        {
          question: 'Will it work in my area?',
          answer: 'Our in-home systems work anywhere with a phone line or cellular connection. Our mobile systems work anywhere with AT&T or T-Mobile coverage.'
        },
        {
          question: 'Can I use it when I travel?',
          answer: 'Our mobile systems work nationwide, so you can take them with you when you travel. In-home systems are designed to stay at your residence.'
        }
      ]
    },
    {
      id: 'push-button',
      title: 'Push The Button Questions',
      questions: [
        {
          question: 'What happens when I push the button?',
          answer: 'When you push your help button, you\'ll be connected to our 24/7 Command Center within seconds. Our trained operators will assess your situation and get you the help you need.'
        },
        {
          question: 'Can I test my button?',
          answer: 'Yes! We encourage weekly testing. Just press your button and tell the operator you\'re testing the system.'
        },
        {
          question: 'What if I press it by accident?',
          answer: 'No problem! Just tell the operator it was an accident. There\'s no charge for false alarms.'
        }
      ]
    },
    {
      id: 'alert1-sends-help',
      title: 'Alert1 Sends Help Questions',
      questions: [
        {
          question: 'How quickly will help arrive?',
          answer: 'Once you press your button, you\'ll be connected to our Command Center. We\'ll immediately contact your designated responders or 911 to get you help as quickly as possible.'
        },
        {
          question: 'Who will you call for me?',
          answer: 'We\'ll call whoever you\'ve designated - family, friends, neighbors, or 911. You choose your responders and the order we call them.'
        }
      ]
    },
    {
      id: 'installation',
      title: 'Installation Questions',
      questions: [
        {
          question: 'How do I install my medical alert system?',
          answer: 'Installation is easy! Most systems can be set up in minutes. Simply plug in the base unit and test your button. We provide step-by-step instructions and our support team is available 24/7 to help.'
        },
        {
          question: 'Can you do an installation?',
          answer: 'Yes! We can do an installation for you. Call 1-877-579-9862 if you require assistance and would like an installation.'
        },
        {
          question: 'How long does installation take?',
          answer: 'Most installations take under 10 minutes and as little as 2-3 minutes. Simply plug in the base unit to power and phone line (if applicable), and test your button.'
        }
      ]
    },
    {
      id: 'operator-responds',
      title: 'Operator Responds Questions',
      questions: [
        {
          question: 'Who responds to my call for help?',
          answer: 'When you order your medical alert system, you get to decide who we call and in what order. When you press your help button you will reach our Command Center. We can then call your emergency responders and/or 911 on your behalf.'
        },
        {
          question: 'Do you have to call 911 first?',
          answer: 'We do not have to dial 911 first. We can call a friend, neighbor or family member if you would like.'
        },
        {
          question: 'When I push my button, who will be helping me?',
          answer: 'You will speak with the operators in our Command Center. They will stay on the line with you and call your emergency responders to get you help.'
        },
        {
          question: 'What is the average response time when someone pushes their button?',
          answer: 'It takes only seconds for our operators to connect to you. Your loved ones get the help they need fast.'
        },
        {
          question: 'How is the response center trained?',
          answer: 'Our Command Center is 5 Diamond Certified by The Monitoring Association. We get regular inspections from Underwriters Laboratory. All our Command Center operators complete the required TMA training every year.'
        },
        {
          question: 'What happens if I set off my medical alarm by accident?',
          answer: 'Tell the Operators at the Command Center that you are just testing your medical alert pendant.'
        },
        {
          question: 'Why does the operator ask to verify my address?',
          answer: 'Operators will verify your address during any non-emergency call that you make. We always want to be sure that we have current location information on your account.'
        }
      ]
    },
    {
      id: 'language',
      title: 'Language Questions',
      questions: [
        {
          question: 'Do your operators speak clear English?',
          answer: 'Yes, our operators are based in the U.S and speak clear English!'
        },
        {
          question: 'Does Alert1 offer service in a foreign language other than English?',
          answer: 'Yes. We have service in 190 languages.'
        },
        {
          question: 'My mother\'s phone number changed and now I need to change the number on the account',
          answer: 'Call us at 1-877-579-9862.'
        },
        {
          question: 'How does your foreign language service work?',
          answer: 'We keep the member\'s language on file and we have interpreters standing by.'
        }
      ]
    },
    {
      id: 'my-account',
      title: 'My Account Questions',
      questions: [
        {
          question: 'Can we add a few other people as emergency responders?',
          answer: 'Yes. You can have up to three emergency responders. You can add them on My Account page or by giving us a call at 1-877-579-9862 and selecting option 2.'
        },
        {
          question: 'How do I update account information?',
          answer: 'You can update it online on My Account page or you can call our Member Care team and they can update it for you. Their number is 1-877-579-9862 option 2.'
        },
        {
          question: 'Who can make changes on the account?',
          answer: 'The member, bill payer, and authorized users can make changes on the account.'
        },
        {
          question: 'How do I get technical support?',
          answer: 'You can call and speak with one of our techs at 1-877-579-9862. They can assist you with anything you need. We have technical support available 24-7.'
        },
        {
          question: 'How do I add a spouse to my account?',
          answer: 'To add a spouse to your account, call our Member Care team at 1-877-579-9862.'
        }
      ]
    },
    {
      id: 'about-alert1',
      title: 'About Alert1 Questions',
      questions: [
        {
          question: 'Who owns Alert1?',
          answer: 'Alert1 is a private company. Our main office is in Williamsport, Pennsylvania, home of the Little League World Series.'
        },
        {
          question: 'What is your address?',
          answer: 'Our address is 1000 Commerce Park Drive, #300 Williamsport, PA 17701'
        },
        {
          question: 'Where are your offices?',
          answer: 'We have offices in Pennsylvania, Idaho, New Jersey, and Texas.'
        },
        {
          question: 'Do you have brick and mortar location?',
          answer: 'Yes we do! You are able to purchase a medical alert system at our Pennsylvania location. The address is: 1000 Commerce Park Drive, Suite 300 Williamsport, Pennsylvania 17701.'
        },
        {
          question: 'What are your customer service hours?',
          answer: 'Our emergency Command Center is open 24/7. For our Member Care and Sales hours, please visit our Contact Us page.'
        },
        {
          question: 'Who makes your equipment?',
          answer: 'We partner with a few different companies. We use manufacturers in Boston, Utah, North Carolina, Israel, Taiwan, and Canada.'
        },
        {
          question: 'Are you affiliated with the AARP?',
          answer: 'We offer AARP discounts to our members.'
        },
        {
          question: 'Can I buy your system from a dealer near me?',
          answer: 'To make sure everything is done to the highest possible standard, Alert1 only does business directly with consumers.'
        },
        {
          question: 'How fast can I receive service once I decide to try your medical alert system? How long does it take to ship it here?',
          answer: 'We ship same day. We can reach most of America in 2-3 days.'
        },
        {
          question: 'Are you rated with the BBB/Consumer Affairs?',
          answer: 'Yes. Alert1 is a BBB Accredited Business with an A+ rating. You can view our BBB profile or visit our review page to see what members say about our medical alert systems.',
          answerContent: <>Yes. Alert1 is a BBB Accredited Business with an A+ rating. You can view our <a href={BBB_PROFILE_URL} target="_blank" rel="nofollow noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>BBB profile</a> or visit our <Link to="/reviews" style={{ color: '#0066cc', textDecoration: 'underline' }}>review page</Link> to see what members say about our medical alert systems.</>
        },
        {
          question: 'Are you UL listed?',
          answer: 'Yes, both our Command Center and our medical alert devices are UL listed.'
        },
        {
          question: 'Are you PCI compliant?',
          answer: 'Yes, Alert1 is Payment Card Industry (PCI) compliant because we have met the requirements set by the PCI Security Standards Council (SSC). This Data Security Standard (DSS) ensures that all companies process, store and transmit credit card information in a secure environment.'
        },
        {
          question: 'Do you outsource?',
          answer: 'Alert1 is a US-based company. We do not outsource your loved one\'s calls to another country.'
        },
        {
          question: 'What makes you better than _____?',
          answer: 'Please see our Why Alert1 page. We think there are 10 main reasons why you should choose us.'
        },
        {
          question: 'Where can I read consumer reviews about your products?',
          answer: 'Customer reports about our products are outstanding. You can view some of our reviews on our review page.',
          answerContent: <>Customer reports about our products are outstanding. You can view some of our reviews on our <Link to="/reviews" style={{ color: '#0066cc', textDecoration: 'underline' }}>review page</Link>.</>
        },
        {
          question: 'How is Alert1 different from LifeAlert?',
          answer: 'Alert1 is a company based in Williamsport, Pennsylvania. We recommend you do your own medical alert comparison.'
        }
      ]
    },
    {
      id: 'other',
      title: 'Other Questions',
      questions: [
        {
          question: 'How fast can I receive service once I decide to try your medical alert system?',
          answer: 'How fast do you need it? We can send it to you right away.'
        },
        {
          question: 'Should I test the device? How often should I test my system? Why should I test?',
          answer: 'Alert1 recommends our members test on a weekly basis. You can test as often as you like. We have unlimited button presses and there is no charge for testing your system. Please tell the operator you are testing your system when the call connects.'
        },
        {
          question: 'How do I test my medical alarm system?',
          answer: 'Press your pendant and wait for an operator to speak with you. Do not press the cancel/reset button. Tell the operator you are testing the system.'
        },
        {
          question: 'Does the medical alert connect to my cellphone?',
          answer: 'We can call your cell phone if you like and you can always access your account online.'
        },
        {
          question: 'Can you send me brochures?',
          answer: 'Sure. Go to brochures.',
          answerContent: <>Sure. Go to our <Link to="/request-brochure" style={{ color: '#0066cc', textDecoration: 'underline' }}>brochures page</Link>.</>
        },
        {
          question: 'Can I refer a friend to your service?',
          answer: 'Yes you can! Learn more on our refer a friend page.',
          answerContent: <>Yes you can! Learn more on our <Link to="/refer-friend" style={{ color: '#0066cc', textDecoration: 'underline' }}>refer a friend page</Link>.</>
        }
      ]
    }
  ], []);

  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace(/^#/, '') || '';
    if (!hash) return;
    const category = categories.find((c) => hash === c.id || hash.startsWith(c.id + '-'));
    if (category) {
      setActiveCategory(category.id);
      const questionSlug = hash.startsWith(category.id + '-') ? hash.slice(category.id.length + 1) : null;
      if (questionSlug) {
        const item = category.questions.find((q) => q.slug === questionSlug);
        if (item) setSelectedQuestion({ question: item.question, answer: item.answer, answerContent: item.answerContent, scrollId: hash });
      }
    }
  }, [location.hash, categories]);

  const toggleCategory = (categoryId: string) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId);
  };

  const handleQuestionClick = (question: string, answer: string, answerContent?: React.ReactNode) => {
    setSelectedQuestion({ question, answer, answerContent });
  };

  // Flatten all questions for search
  const allQuestions = categories.flatMap((cat) =>
    cat.questions.map((q) => ({ ...q, categoryTitle: cat.title }))
  );

  // Search results - match question or answer text (case-insensitive)
  const searchResults = searchQuery.trim()
    ? allQuestions.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Get popular questions from all categories
  const popularQuestions = [
    { question: 'What is the cost?', answer: 'See our pricing page or call us at 1-877-579-9862.' },
    { question: 'How does it work?', answer: 'Press your button, connect to our monitoring center, and help is dispatched immediately.' },
    { question: 'Do you offer refunds?', answer: 'Alert1 does not require long term contracts. Any user may cancel service at any time, simply by returning the Alert1 equipment. However, some users agree to contracts of a specified length (for example, annual plans) in exchange for a substantial discount. Such users may cancel their service early, but Alert1 does not offer refunds in such circumstances unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.' },
    { question: 'How do I update account information?', answer: 'You can update it online on My Account page or you can call our Member Care team and they can update it for you. Their number is 1-877-579-9862 option 2.' },
    { question: 'Why are the lights flashing on my device?', answer: 'Flashing lights indicate different statuses. Please refer to your user manual or call us at 1-877-579-9862 for assistance.' }
  ];

  return (
    <div className="faq-page">
      {/* Main FAQ Section with gradient background */}
      <section className="faq-hero-gradient">
        {/* Title Section - Full Width */}
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '1rem 2rem 0.25rem 2rem' }}>
          <h1 style={{ color: 'white', fontSize: '2.5rem', fontWeight: '400', margin: 0, lineHeight: '1.2' }}>Medical Alert Help</h1>
        </div>
        
        {/* Two Column Section */}
        <div className="faq-container" style={{ display: 'flex', gap: '1.5rem', maxWidth: '1100px', margin: '0 auto', padding: '0.5rem 2rem 1rem 2rem' }}>
          {/* Sidebar */}
          <aside className="faq-sidebar" style={{ background: 'white', padding: '1.25rem 1rem', borderRadius: '16px', width: '240px', flexShrink: 0, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '700', marginTop: 0, marginBottom: '0.75rem' }}>Categories</h2>
            <nav className="faq-categories">
              {categories.map((category) => (
                <div key={category.id} id={category.id} className="faq-category-wrapper">
                  <button
                    className={`faq-category-btn ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    {category.title}
                    <span className="faq-chevron">{activeCategory === category.id ? '▼' : '›'}</span>
                  </button>
                  {activeCategory === category.id && (
                    <div className="faq-category-questions">
                      {category.questions.map((item, index) => (
                        <button
                          key={index}
                          className="faq-question-link"
                          onClick={() => handleQuestionClick(item.question, item.answer, item.answerContent)}
                        >
                          {item.question}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main style={{ flex: 1 }}>
            {/* Search Bar - in white box */}
            <div style={{ background: 'white', borderRadius: '50px', padding: '0.6rem 1.5rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#666', marginRight: '1rem' }}>
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="How can we help you?"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '1.1rem', color: '#333', background: 'transparent' }}
              />
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '1rem', padding: '0.5rem 1rem' }}
              >
                Clear ✕
              </button>
            </div>

            {/* Search Results - shown when user has typed a query */}
            {searchQuery.trim() && (
              <div style={{ background: 'white', padding: '1.5rem', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '1rem' }}>
                <h2 style={{ fontSize: '1.3rem', marginBottom: '0.75rem', color: '#333', fontWeight: '700' }}>
                  Search Results{searchResults.length > 0 ? ` (${searchResults.length})` : ''}
                </h2>
                {searchResults.length > 0 ? (
                  <div className="faq-questions">
                    {searchResults.map((item, index) => (
                      <div key={index} className="faq-popular-link">
                        <button
                          type="button"
                          onClick={() => handleQuestionClick(item.question, item.answer, item.answerContent)}
                        >
                          {item.question}
                        </button>
                        <span style={{ fontSize: '0.85rem', color: '#888', marginLeft: '0.5rem' }}>
                          — {item.categoryTitle}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#666', margin: 0 }}>No FAQ items match &quot;{searchQuery}&quot;. Try different words.</p>
                )}
              </div>
            )}

            {/* Quick Action Icons - in white boxes */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <a href="/request-brochure" className="faq-icon-btn">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h4l3 3 3-3h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                </svg>
                <span>Request Brochure</span>
              </a>
              <a href="/my-account" className="faq-icon-btn">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                </svg>
                <span>My Account</span>
              </a>
              <a href="/troubleshooting" className="faq-icon-btn">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <span>Troubleshooting</span>
              </a>
              <button className="faq-icon-btn" onClick={() => { toggleCategory('installation'); window.scrollTo({ top: 400, behavior: 'smooth' }); }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
                </svg>
                <span>Set Up</span>
              </button>
              <button className="faq-icon-btn" onClick={() => { toggleCategory('about-alert1'); window.scrollTo({ top: 400, behavior: 'smooth' }); }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4m0-4h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>General Information</span>
              </button>
              <a href="/refer-friend" className="faq-icon-btn">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
                <span>Refer a Friend</span>
              </a>
            </div>

            {/* Selected Question/Answer Box - appears between icons and popular questions */}
            {selectedQuestion && (
              <div id={selectedQuestion.scrollId} style={{ background: 'white', padding: '1.5rem', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '1rem' }}>
                <h2 style={{ fontSize: '1.3rem', marginBottom: '0.75rem', color: '#333', fontWeight: '700' }}>{selectedQuestion.question}</h2>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.5', color: '#555' }}>{selectedQuestion.answerContent ?? selectedQuestion.answer}</p>
              </div>
            )}

            {/* Popular Questions Box - always visible */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#333', fontWeight: '700' }}>Popular Questions:</h2>
              <div className="faq-questions">
                {popularQuestions.map((item, index) => (
                  <div key={index} className="faq-popular-link">
                    <button type="button" onClick={(e) => {
                      e.preventDefault();
                      handleQuestionClick(item.question, item.answer, (item as FAQItem).answerContent);
                    }}>
                      {item.question}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </section>

      {/* CTA Section */}
      <section className="faq-cta-section">
        <div className="container">
          <h2>Still Have Questions?</h2>
          <p>Our friendly team is here to help you find the perfect medical alert solution.</p>
          <div className="cta-buttons">
            <a href="tel:1-877-579-9862" className="btn btn-primary">Call 1-877-579-9862</a>
            <a href="/contact" className="btn-secondary">Contact Us</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;